import { banStatusStrings } from 'constants/strings'
import type { TBanStatusSummary } from 'types'
import { formatNumber } from 'utils'
import { formatPausedRate, getCategoryLabel } from './banStatusView'
import { BanStatusLabel } from './BanStatusLabel'

type BanStatusSummaryProps = {
  summary: TBanStatusSummary
}

type Tile = {
  label: string
  value: string
  help: keyof typeof banStatusStrings.help
  /** 危険配色にするか。件数ではなく呼び出し側が意味で決める */
  highlighted?: boolean
}

export function BanStatusSummary({ summary }: BanStatusSummaryProps) {
  const tiles: Tile[] = [
    { label: banStatusStrings.summary.total, value: formatNumber(summary.total), help: 'total' },
    {
      label: banStatusStrings.summary.active,
      value: formatNumber(summary.active),
      help: 'active',
    },
    { label: banStatusStrings.summary.full, value: formatNumber(summary.full), help: 'full' },
    {
      label: getCategoryLabel('autoPaused'),
      value: formatNumber(summary.autoPaused),
      help: 'autoPaused',
      highlighted: summary.autoPaused > 0,
    },
    {
      label: getCategoryLabel('manualPaused'),
      value: formatNumber(summary.manualPaused),
      help: 'manualPaused',
    },
    {
      label: getCategoryLabel('unlinked'),
      value: formatNumber(summary.unlinked),
      help: 'unlinked',
    },
    {
      label: getCategoryLabel('webhookInvalid'),
      value: formatNumber(summary.webhookInvalid),
      help: 'webhookInvalid',
      highlighted: summary.webhookInvalid > 0,
    },
    {
      label: getCategoryLabel('notAllocatable'),
      value: formatNumber(summary.notAllocatable),
      help: 'notAllocatable',
      highlighted: summary.notAllocatable > 0,
    },
    {
      label: banStatusStrings.summary.allocatable,
      value: formatNumber(summary.allocatable),
      help: 'allocatable',
    },
    {
      label: banStatusStrings.summary.pausedRate,
      value: formatPausedRate(summary),
      help: 'pausedRate',
    },
  ]

  return (
    <dl
      aria-label={banStatusStrings.summary.heading}
      className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          data-testid={`summary-tile-${tile.help}`}
          className={`grid content-start gap-1 rounded-2xl border p-4 ${
            tile.highlighted
              ? 'border-danger-border bg-danger-soft'
              : 'border-border/70 bg-white'
          }`}
        >
          <dt className="text-xs text-muted">
            <BanStatusLabel label={tile.label} help={tile.help} />
          </dt>
          <dd
            className={`break-all text-2xl font-semibold tabular-nums sm:text-3xl ${
              tile.highlighted ? 'text-danger' : 'text-ink'
            }`}
          >
            {tile.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}
